import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Rating,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { submit_feedback } from "../../api/agent";
import { usePlaygroundContext } from "../Playground";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const [rating, setRating] = useState<number | null>(0);
  const [feedback, setFeedback] = useState<string>("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = () => {
    submit_feedback({
      rating: rating,
      comment: feedback,
      email: playground.companyEmail,
    })
      .then(() => {
        setOpenSnackbar(true); // Show snackbar on success
        onClose();
      })
      .catch(() => {
        setOpenSnackbar(true); // Show snackbar even on failure
      })
      .finally(() => {
        onClose(); // Close modal
      });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          Share your feedback
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: "black", position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            How would you rate your experience?
          </Typography>
          <Rating
            name="rating"
            value={rating}
            onChange={(event, newValue) => setRating(newValue)}
            size="large"
          />
          <Typography
            variant="subtitle1"
            sx={{ mt: 2, textAlign: "left", ml: 2 }}
          >
            Tell us about your experience
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{
              backgroundColor: "#f7f7f9",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "1px solid rgba(66, 73, 101, 1)",
                },
                "&:hover fieldset": {
                  border: "1px solid rgba(66, 73, 101, 1)",
                },
                "&.Mui-focused fieldset": {
                  border: "1px solid rgba(66, 73, 101, 1)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{ display: "flex", justifyContent: "center", padding: "16px" }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0066F4",
              color: "#FFFFFF",
              borderRadius: "24px",
              padding: "8px 32px",
              "&:hover": {
                backgroundColor: "#0056D2",
              },
            }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar with dynamic message and mobile responsiveness */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: 10,
          left: 10,
          right: 0,
          width: isMobile ? "95%" : "auto",
        }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
        Your feedback has been submitted successfully. We value your opinion!
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackModal;
